// src/services/apiV2.ts - March 16, 2026
// V2 API Service: Routes through Netlify Functions (new architecture)
//
// MULTI-TENANT: Every request sends x-tenant-slug header so the
// Netlify Function knows which agent to call.
//
// V2.1 CHANGE: Added getDashboardAll() — single consolidated call replaces
// 6 separate function calls (sales-dashboard, sales-by-rep, ar-aging,
// sales-trend, salesmen-list, categories-list). Prevents concurrent
// HTTP requests that overload the agent's ADODB connection.

import axios, { type AxiosInstance } from 'axios';
import type {
  ApiResponse, SalesRepSummary, ARSummary, DashboardStats,
  Filters, HebrewDate, TrendDataPoint,
  SalesDetailResponse, ARDetailResponse, ARDrilldownResponse, CustomerInvoiceResponse, AnalyticsFilters,
  CacheFreshnessResponse,
  SalesByRepKpisResponse, SalesByRepTrendResponse,
  InventoryActionQueueResponse, InventoryTransitionFeedResponse,
  InventorySkuDetailResponse, InventoryStatusResponse,
  InventoryInsightResponse,
  ContainerSummary, ContainerDetail,
  ContainerCreateRequest, ContainerCreateLine, ContainerPatchRequest,
  AuditHistoryEvent, PurchasingPipelineResponse,
  AiOrderSummary, AiOrderDetail, AiOrderPatchRequest,
  OrdersSheetResponse, SalesTrendResponse, SalesGapsResponse,
  QbdPnlResponse, QbdPnlSummaryResponse, QbdExpenseBreakdownResponse,
  QbdAgingResponse, QbdStatusResponse, QbdSyncResponse,
  QbdBalanceSheetResponse, QbdAdjustment, QbdBudgetRow, QbdBudgetVsActualResponse,
  QbdPayrollSummaryResponse, QbdPayrollEmployeesResponse, QbdTimecardsResponse,
  SevenVs28Response, YoyMatrixResponse, PaymentsBehaviorResponse, ArHistoryResponse,
  OpsSniResponse, OpsVoidResponse, OpsShortResponse,
  SalesVsPaymentsResponse, CustomerCountsResponse, GpAnalysisResponse,
  DailySnapshotResponse, SavedView, AnalystReply, CategoryMatrixResponse, CustomerWeekGridResponse, QbdKpiPanelResponse, CcFeeReconResponse, DailyOrderSnapshotResponse, CustomerLedgerResponse, TransferExceptionsResponse, CustomerInsightsResponse, MarginAnalysisResponse,
  ArFocusResponse, ArPaymentBucketsResponse,
} from '../types';
import type {
  AgingBasis, AgingResponse, ApBillsResponse, ApBillDetailResponse, VendorSpendResponse,
  VendorPriceChangesResponse, VendorPriceHistoryResponse, VendorScorecardResponse, QboDiffResponse,
} from '../types/payables';
import type { QboReport } from '../types/qboReports';

/** Filter-pane slicers (analytics.filterpane) — see pages/analytics/filterPane.tsx. */
type PaneParams = Record<string, string | undefined>;

const createV2Client = (token?: string, tenantSlug?: string): AxiosInstance => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantSlug) headers['x-tenant-slug'] = tenantSlug;

  const client = axios.create({ baseURL: '', timeout: 60000, headers });
  client.interceptors.response.use(
    r => r,
    error => { console.error('[apiV2] Error:', error.response?.data || error.message); throw error; }
  );
  return client;
};

// ── Dashboard All Response Type ──
export interface DashboardAllResponse {
  stats: DashboardStats;
  salesByRep: SalesRepSummary[];
  arAging: ARSummary;
  trend: TrendDataPoint[];
  salesmen: Array<{ code: string; name: string }>;
  categories: string[];
}

class ApiV2Service {
  client: AxiosInstance;
  private _token: string | null = null;
  private _slug: string | null = null;
  private _agentBaseUrl: string | null = null;
  private _agentClient: AxiosInstance | null = null;

  constructor() {
    this.client = createV2Client();
  }

  private rebuild() {
    this.client = createV2Client(this._token || undefined, this._slug || undefined);
  }

  setAuthToken(token: string) {
    this._token = token;
    this._agentClient = null; // Force rebuild with new token
    this.rebuild();
  }

  setTenantSlug(slug: string) {
    this._slug = slug;
    this.rebuild();
  }

  setAgentBaseUrl(url: string) {
    this._agentBaseUrl = url;
    this._agentClient = null; // Force rebuild
  }

  private getAgentClient(): AxiosInstance {
    if (this._agentClient) return this._agentClient;
    if (!this._agentBaseUrl) throw new Error('Agent URL not configured');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this._token) headers['Authorization'] = `Bearer ${this._token}`;
    this._agentClient = axios.create({
      baseURL: this._agentBaseUrl,
      timeout: 300000, // 5 min — no Netlify timeout constraint
      headers,
    });
    this._agentClient.interceptors.response.use(
      r => r,
      error => { console.error('[apiV2:agent] Error:', error.response?.data || error.message); throw error; }
    );
    return this._agentClient;
  }

  /** Execute a recipe directly on the agent (bypasses Netlify timeout) */
  private async agentRecipe<T>(recipe: string, params: Record<string, string | undefined>): Promise<T> {
    const clean: Record<string, string> = {};
    Object.entries(params).forEach(([k, v]) => { if (v) clean[k] = v; });
    const resp = await this.getAgentClient().post<{ success: boolean; data: T; error?: string }>(
      '/api/recipe', { recipe, params: clean }
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Recipe failed');
    return resp.data.data;
  }

  // --- Health ---
  async healthCheck(): Promise<boolean> {
    try { const r = await this.client.get('/.netlify/functions/agent-health'); return r.data?.status === 'ok'; } catch { return false; }
  }

  // --- Dashboard (consolidated — ONE call, direct to agent) ---
  async getDashboardAll(f?: Filters): Promise<DashboardAllResponse> {
    return this.agentRecipe<DashboardAllResponse>('dashboard-all', {
      salesman: f?.salesman,
      category: f?.category,
    });
  }

  // --- Individual dashboard endpoints (kept for standalone use) ---
  async getDashboardStats(f?: Filters): Promise<DashboardStats> {
    const p = new URLSearchParams();
    if (f?.salesman) p.append('salesman', f.salesman);
    if (f?.category) p.append('category', f.category);
    return (await this.client.get<ApiResponse<DashboardStats>>(`/.netlify/functions/sales-dashboard?${p}`)).data.data;
  }

  async getSalesByRep(f?: Filters): Promise<SalesRepSummary[]> {
    const p = new URLSearchParams();
    if (f?.salesman) p.append('salesman', f.salesman);
    if (f?.category) p.append('category', f.category);
    if (f?.startDate) p.append('startDate', f.startDate);
    if (f?.endDate) p.append('endDate', f.endDate);
    return (await this.client.get<ApiResponse<SalesRepSummary[]>>(`/.netlify/functions/sales-by-rep?${p}`)).data.data;
  }

  async getARaging(f?: Filters): Promise<ARSummary> {
    const p = new URLSearchParams();
    if (f?.salesman) p.append('salesman', f.salesman);
    if (f?.category) p.append('category', f.category);
    return (await this.client.get<ApiResponse<ARSummary>>(`/.netlify/functions/ar-aging?${p}`)).data.data;
  }

  async getSalesTrend(f?: Filters): Promise<TrendDataPoint[]> {
    const p = new URLSearchParams();
    if (f?.salesman) p.append('salesman', f.salesman);
    return (await this.client.get<ApiResponse<TrendDataPoint[]>>(`/.netlify/functions/sales-trend?${p}`)).data.data;
  }

  async getSalesmenList(): Promise<Array<{ code: string; name: string }>> {
    return (await this.client.get<ApiResponse<Array<{ code: string; name: string }>>>('/.netlify/functions/salesmen-list')).data.data;
  }

  async getCategoriesList(): Promise<string[]> {
    return (await this.client.get<ApiResponse<string[]>>('/.netlify/functions/categories-list')).data.data;
  }

  // --- Hebrew Date ---
  async getHebrewDate(date?: string): Promise<HebrewDate> {
    const p = date ? `?date=${date}` : '';
    return (await this.client.get<ApiResponse<HebrewDate>>(`/.netlify/functions/hebrew-date${p}`)).data.data;
  }

  // --- Analytics Detail (direct to agent — no Netlify timeout) ---
  async getAnalyticsFilters(): Promise<AnalyticsFilters> {
    return this.agentRecipe<AnalyticsFilters>('analytics-filters', {});
  }

  async getSalesDetail(params: Record<string, string | undefined>): Promise<SalesDetailResponse> {
    return this.agentRecipe<SalesDetailResponse>('analytics-sales-detail', params);
  }

  /**
   * Order Sheet — line-level open/invoiced order browser (recipe: orders-sheet).
   * All params are strings; the server defaults dateFrom/dateTo to the last 90
   * days and `status=all` (which excludes voids). limit default 100, max 500.
   */
  async getOrdersSheet(params: Record<string, string | undefined>): Promise<OrdersSheetResponse> {
    return this.agentRecipe<OrdersSheetResponse>('orders-sheet', params);
  }

  /**
   * Sales Trends — bucketed time series (recipe: sales-trend). NOTE: distinct
   * from the legacy Netlify `sales-trend` function above (getSalesTrend);
   * this one runs the agent recipe with the shared analytics filter set.
   */
  async getSalesTrendSeries(params: Record<string, string | undefined>): Promise<SalesTrendResponse> {
    return this.agentRecipe<SalesTrendResponse>('sales-trend', params);
  }

  /**
   * Lost Business — churn / dropped-item / never-bought finder (recipe:
   * sales-gaps). `targetItem` is REQUIRED when mode=never_bought.
   */
  async getSalesGaps(params: Record<string, string | undefined>): Promise<SalesGapsResponse> {
    return this.agentRecipe<SalesGapsResponse>('sales-gaps', params);
  }

  /**
   * 7 vs 28 Days — this-week vs trailing-4-week-average comparison by
   * dimension (recipe: sales-7v28). dimension defaults server-side.
   */
  async getSevenVs28(params: { dimension?: string; measure?: string } = {}, pane: PaneParams = {}): Promise<SevenVs28Response> {
    return this.agentRecipe<SevenVs28Response>('sales-7v28', {
      ...pane,
      dimension: params.dimension,
      measure:   params.measure,
    });
  }

  /**
   * YoY matrix — multi-year year-over-year comparison bucketed by
   * week/month/quarter/year on a Gregorian or Hebrew calendar (recipe:
   * sales-yoy-matrix).
   */
  async getYoyMatrix(params: { dimension?: string; bucket?: string; calendar?: string; yearsBack?: number; measure?: string; ytd?: boolean } = {}, pane: PaneParams = {}): Promise<YoyMatrixResponse> {
    return this.agentRecipe<YoyMatrixResponse>('sales-yoy-matrix', {
      ...pane,
      dimension: params.dimension,
      bucket:    params.bucket,
      calendar:  params.calendar,
      yearsBack: params.yearsBack != null ? String(params.yearsBack) : undefined,
      measure:   params.measure,
      ytd:       params.ytd ? 'true' : undefined,
    });
  }

  /**
   * Payments behavior vs terms — weekly before/on-time/after/unapplied
   * split plus day-of-week distribution (recipe: payments-behavior).
   */
  async getPaymentsBehavior(params: { weeks?: number } = {}): Promise<PaymentsBehaviorResponse> {
    return this.agentRecipe<PaymentsBehaviorResponse>('payments-behavior', {
      weeks: params.weeks != null ? String(params.weeks) : undefined,
    });
  }

  /**
   * A/R aging history from the agent's daily snapshots (recipe: ar-history).
   * Sparse until the agent has been snapshotting for a while.
   */
  async getArHistory(params: { granularity?: string; months?: number; split?: string } = {}): Promise<ArHistoryResponse> {
    return this.agentRecipe<ArHistoryResponse>('ar-history', {
      granularity: params.granularity,
      months:      params.months != null ? String(params.months) : undefined,
      split:       params.split || undefined,
    });
  }

  /** Ops audit — shipped-not-invoiced orders (recipe: ops-shipped-not-invoiced). */
  async getOpsShippedNotInvoiced(): Promise<OpsSniResponse> {
    return this.agentRecipe<OpsSniResponse>('ops-shipped-not-invoiced', {});
  }

  /** Ops audit — voided orders in a date range (recipe: ops-void-orders). */
  async getOpsVoidOrders(params: { dateFrom?: string; dateTo?: string } = {}): Promise<OpsVoidResponse> {
    return this.agentRecipe<OpsVoidResponse>('ops-void-orders', {
      dateFrom: params.dateFrom,
      dateTo:   params.dateTo,
    });
  }

  /** Ops audit — vendor/item short-shipments (recipe: ops-short-shipped). */
  async getOpsShortShipped(params: {
    dateFrom?: string; dateTo?: string; groupBy?: string; source?: string;
    itemCategory?: string; itemSubcategory?: string;   // agent 3.22.0+
  } = {}): Promise<OpsShortResponse> {
    return this.agentRecipe<OpsShortResponse>('ops-short-shipped', {
      dateFrom: params.dateFrom,
      dateTo:   params.dateTo,
      groupBy:  params.groupBy,
      source:   params.source,
      itemCategory:    params.itemCategory || undefined,
      itemSubcategory: params.itemSubcategory || undefined,
    });
  }

  /**
   * Sales vs Payments — sales billed vs payments received per period
   * (recipe: sales-vs-payments), plus a by-dimension breakdown for the
   * whole window.
   */
  async getSalesVsPayments(params: { bucket?: string; months?: number; calendar?: string; dimension?: string } = {}): Promise<SalesVsPaymentsResponse> {
    return this.agentRecipe<SalesVsPaymentsResponse>('sales-vs-payments', {
      bucket:    params.bucket,
      months:    params.months != null ? String(params.months) : undefined,
      calendar:  params.calendar,
      dimension: params.dimension,
    });
  }

  /**
   * Customer Counts — distinct buying customers per month by customer
   * category (recipe: customer-counts).
   */
  async getCustomerCounts(params: { months?: number } = {}): Promise<CustomerCountsResponse> {
    return this.agentRecipe<CustomerCountsResponse>('customer-counts', {
      months: params.months != null ? String(params.months) : undefined,
    });
  }

  /** Invoices / penetration matrix by category × period (recipe: sales-category-matrix, agent 3.20.0+). */
  async getCategoryMatrix(params: { rowDim?: string; bucket?: string; calendar?: string; yearOffset?: number } = {}, pane: PaneParams = {}): Promise<CategoryMatrixResponse> {
    return this.agentRecipe<CategoryMatrixResponse>('sales-category-matrix', {
      ...pane,
      rowDim:     params.rowDim,
      bucket:     params.bucket,
      calendar:   params.calendar,
      yearOffset: params.yearOffset != null ? String(params.yearOffset) : undefined,
    });
  }

  /** Customer × item category × week purchase grid (recipe: customer-week-grid, agent 3.20.0+). */
  async getCustomerWeekGrid(params: { weeks?: number; measure?: string; calendar?: string; limit?: number } = {}, pane: PaneParams = {}): Promise<CustomerWeekGridResponse> {
    return this.agentRecipe<CustomerWeekGridResponse>('customer-week-grid', {
      ...pane,
      weeks:    params.weeks != null ? String(params.weeks) : undefined,
      measure:  params.measure,
      calendar: params.calendar,
      limit:    params.limit != null ? String(params.limit) : undefined,
    });
  }

  /** Open orders for one delivery date by category and driver (recipe: daily-order-snapshot, agent 3.20.0+). */
  async getDailyOrderSnapshot(params: { date?: string } = {}): Promise<DailyOrderSnapshotResponse> {
    return this.agentRecipe<DailyOrderSnapshotResponse>('daily-order-snapshot', { date: params.date });
  }

  /** Card fees charged (Advantage) vs paid (QuickBooks) by month (recipe: cc-fee-recon, agent 3.20.0+). */
  async getCcFeeRecon(params: { months?: number } = {}): Promise<CcFeeReconResponse> {
    return this.agentRecipe<CcFeeReconResponse>('cc-fee-recon', {
      months: params.months != null ? String(params.months) : undefined,
    });
  }

  /** AR Focus — window sales/payments vs open A/R inside and past the window (recipe: ar-focus, agent 3.20.0+). */
  async getArFocus(params: { days?: number; dimension?: string } = {}, pane: PaneParams = {}): Promise<ArFocusResponse> {
    return this.agentRecipe<ArFocusResponse>('ar-focus', {
      ...pane,
      days:      params.days != null ? String(params.days) : undefined,
      dimension: params.dimension,
    });
  }

  /** Payments by the aging bucket they cleared from (recipe: ar-payment-buckets, agent 3.20.0+). */
  async getArPaymentBuckets(params: { weeks?: number; dimension?: string; minDays?: string; maxDays?: string } = {}, pane: PaneParams = {}): Promise<ArPaymentBucketsResponse> {
    return this.agentRecipe<ArPaymentBucketsResponse>('ar-payment-buckets', {
      ...pane,
      weeks:     params.weeks != null ? String(params.weeks) : undefined,
      dimension: params.dimension,
      minDays:   params.minDays,
      maxDays:   params.maxDays,
    });
  }

  /**
   * GP Analysis — estimated gross profit by dimension over a date range
   * (recipe: gp-analysis). The cost basis (line cost vs average-cost
   * estimate) is explained in the response's `note` — surface it in the UI.
   */
  async getGpAnalysis(params: { dimension?: string; dateFrom?: string; dateTo?: string } = {}, pane: PaneParams = {}): Promise<GpAnalysisResponse> {
    return this.agentRecipe<GpAnalysisResponse>('gp-analysis', {
      ...pane,
      dimension: params.dimension,
      dateFrom:  params.dateFrom,
      dateTo:    params.dateTo,
    });
  }

  /**
   * Daily Snapshot — one-day business summary (recipe: daily-snapshot).
   * Called with no date, the server defaults to the most recent day with
   * data (usually yesterday) and echoes the chosen date back.
   */
  async getDailySnapshot(params: { date?: string } = {}): Promise<DailySnapshotResponse> {
    return this.agentRecipe<DailySnapshotResponse>('daily-snapshot', {
      date: params.date,
    });
  }

  // --- Saved views (agent /api/views — direct endpoints, not recipes) ---
  // Per-page bookmarked control states used by the SavedViews component.
  // Older agents don't have these routes; the component hides itself when
  // the GET fails.

  async getSavedViews(page: string): Promise<SavedView[]> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: SavedView[]; error?: string }>(
      `/api/views?page=${encodeURIComponent(page)}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'List views failed');
    return resp.data.data;
  }

  /** Upsert — omit `id` to create; include it to rename/overwrite. */
  async putSavedView(body: { id?: string; page: string; name: string; state: string }): Promise<{ id: string }> {
    const resp = await this.getAgentClient().put<{ success: boolean; data: { id: string }; error?: string }>(
      '/api/views', body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Save view failed');
    return resp.data.data;
  }

  async deleteSavedView(id: string): Promise<void> {
    const resp = await this.getAgentClient().delete<{ success: boolean; error?: string }>(
      `/api/views/${encodeURIComponent(id)}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Delete view failed');
  }

  // --- Analytics Assistant (agent /api/analyst) ---
  // Conversational AI analyst. status is a lightweight gate (404 => the
  // module isn't enabled on this tenant's agent); chat runs a full agentic
  // turn on the agent (it may call several recipes), so it can take a while —
  // the agent client already allows 5-minute calls.

  /** Whether the analyst is enabled on this tenant's agent. 404 => disabled. */
  async getAnalystStatus(): Promise<{ enabled: boolean }> {
    try {
      const resp = await this.getAgentClient().get<{ success: boolean; data: { enabled: boolean }; error?: string }>(
        '/api/analyst/status'
      );
      if (!resp.data.success) return { enabled: false };
      return resp.data.data;
    } catch (err: any) {
      // 404 => analyst feature not present on this tenant/agent.
      if (err?.response?.status === 404) return { enabled: false };
      throw err;
    }
  }

  /**
   * Send the full conversation (role + content only) and get the analyst's
   * reply. The agent runs the same recipes the dashboards use and returns the
   * answer as markdown plus the tool steps it took.
   */
  async analystChat(messages: { role: string; content: string }[]): Promise<AnalystReply> {
    const resp = await this.getAgentClient().post<{ success: boolean; data: AnalystReply; error?: string }>(
      '/api/analyst/chat', { messages }
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Analyst chat failed');
    return resp.data.data;
  }

  async getARDetail(params: Record<string, string | undefined>): Promise<ARDetailResponse> {
    return this.agentRecipe<ARDetailResponse>('analytics-ar-detail', params);
  }

  /** One customer's A/R ledger with running balance (recipe: customer-ledger, agent 3.20.0+). */
  async getCustomerLedger(params: { code: number; months?: number }): Promise<CustomerLedgerResponse> {
    return this.agentRecipe<CustomerLedgerResponse>('customer-ledger', {
      code:   String(params.code),
      months: params.months != null ? String(params.months) : undefined,
    });
  }

  /** Warehouse transfer exceptions: item swaps, qty diffs, staging vs posted, unposted (recipe: transfer-exceptions, agent 3.21.0+). */
  async getTransferExceptions(params: {
    days?: number; fromWh?: string; toWh?: string; type?: string; unpostedDays?: number;
    itemCategory?: string; itemSubcategory?: string;
  } = {}): Promise<TransferExceptionsResponse> {
    return this.agentRecipe<TransferExceptionsResponse>('transfer-exceptions', {
      days:            params.days != null ? String(params.days) : undefined,
      fromWh:          params.fromWh || undefined,
      toWh:            params.toWh || undefined,
      type:            params.type && params.type !== 'all' ? params.type : undefined,
      unpostedDays:    params.unpostedDays != null ? String(params.unpostedDays) : undefined,
      itemCategory:    params.itemCategory || undefined,
      itemSubcategory: params.itemSubcategory || undefined,
    });
  }

  /** Customer activity, growth / decline status and segments (recipe: customer-insights, agent 3.22.0+). */
  async getCustomerInsights(params: {
    months?: number; salesman?: string; customerCategory?: string; area?: string; status?: string; search?: string; limit?: number;
  } = {}, pane: PaneParams = {}): Promise<CustomerInsightsResponse> {
    return this.agentRecipe<CustomerInsightsResponse>('customer-insights', {
      ...pane,
      months:           params.months != null ? String(params.months) : undefined,
      salesman:         params.salesman || undefined,
      customerCategory: params.customerCategory || undefined,
      area:             params.area || undefined,
      status:           params.status || undefined,
      search:           params.search || undefined,
      limit:            params.limit != null ? String(params.limit) : undefined,
    });
  }

  /** Margins by dimension vs a target %, prior-period erosion, lines sold below cost (recipe: margin-analysis, agent 3.22.0+). */
  async getMarginAnalysis(params: {
    dateFrom?: string; dateTo?: string; dimension?: string; targetPct?: number;
  } = {}, pane: PaneParams = {}): Promise<MarginAnalysisResponse> {
    return this.agentRecipe<MarginAnalysisResponse>('margin-analysis', {
      ...pane,
      dateFrom:  params.dateFrom,
      dateTo:    params.dateTo,
      dimension: params.dimension,
      targetPct: params.targetPct != null ? String(params.targetPct) : undefined,
    });
  }

  // --- A/P + purchasing (agent 3.24.0+) ---
  /** A/P aging from QuickBooks Online's open bills (Advantage records no bill payments). 404 = QBO module off. */
  async getApAging(params: { basis?: AgingBasis; vendor?: number } = {}): Promise<AgingResponse> {
    const q = new URLSearchParams({ basis: params.basis ?? 'billDate' });
    if (params.vendor != null) q.append('vendor', String(params.vendor));
    const resp = await this.getAgentClient().get<{ success: boolean; data: AgingResponse; error?: string }>(`/api/qbo/ap-aging?${q}`);
    if (!resp.data.success) throw new Error(resp.data.error || 'QuickBooks A/P aging failed');
    return resp.data.data;
  }

  async getArAging(params: { basis?: AgingBasis; customer?: number; salesman?: string; defaultTermsDays?: number } = {}): Promise<AgingResponse> {
    return this.agentRecipe<AgingResponse>('ar-aging', {
      basis: params.basis,
      customer: params.customer != null ? String(params.customer) : undefined,
      salesman: params.salesman || undefined,
      defaultTermsDays: params.defaultTermsDays != null ? String(params.defaultTermsDays) : undefined,
    });
  }

  async getApBills(params: { dateFrom?: string; dateTo?: string; vendor?: number; status?: string; q?: string } = {}): Promise<ApBillsResponse> {
    return this.agentRecipe<ApBillsResponse>('ap-bills', {
      dateFrom: params.dateFrom, dateTo: params.dateTo,
      vendor: params.vendor != null ? String(params.vendor) : undefined,
      status: params.status, q: params.q || undefined,
    });
  }

  async getApBill(po: string): Promise<ApBillDetailResponse> {
    return this.agentRecipe<ApBillDetailResponse>('ap-bills', { po });
  }

  async getVendorSpend(params: { dateFrom?: string; dateTo?: string; period?: string; vendor?: number } = {}): Promise<VendorSpendResponse> {
    return this.agentRecipe<VendorSpendResponse>('vendor-spend', {
      dateFrom: params.dateFrom, dateTo: params.dateTo, period: params.period,
      vendor: params.vendor != null ? String(params.vendor) : undefined,
    });
  }

  async getVendorPriceChanges(params: { dateFrom?: string; dateTo?: string; vendor?: number } = {}): Promise<VendorPriceChangesResponse> {
    return this.agentRecipe<VendorPriceChangesResponse>('vendor-price-changes', {
      dateFrom: params.dateFrom, dateTo: params.dateTo,
      vendor: params.vendor != null ? String(params.vendor) : undefined,
    });
  }

  async getVendorPriceHistory(item: string, params: { dateFrom?: string; dateTo?: string } = {}): Promise<VendorPriceHistoryResponse> {
    return this.agentRecipe<VendorPriceHistoryResponse>('vendor-price-changes', { item, dateFrom: params.dateFrom, dateTo: params.dateTo });
  }

  async getVendorScorecard(params: { dateFrom?: string; dateTo?: string; graceDays?: number } = {}): Promise<VendorScorecardResponse> {
    return this.agentRecipe<VendorScorecardResponse>('vendor-scorecard', {
      dateFrom: params.dateFrom, dateTo: params.dateTo,
      graceDays: params.graceDays != null ? String(params.graceDays) : undefined,
    });
  }

  /** Advantage vs QuickBooks Online, document by document (agent 3.24.0+, QBO tenants). */
  async getQboDiff(params: { entity: 'bills' | 'invoices'; from: string; to: string; includeMatched?: boolean }): Promise<QboDiffResponse> {
    const q = new URLSearchParams({ entity: params.entity, from: params.from, to: params.to });
    if (params.includeMatched) q.append('includeMatched', 'true');
    const resp = await this.getAgentClient().get<{ success: boolean; data: QboDiffResponse; error?: string }>(`/api/qbo/diff?${q}`);
    if (!resp.data.success) throw new Error(resp.data.error || 'QuickBooks diff failed');
    return resp.data.data;
  }

  async getQboPnl(params: { from: string; to: string; summarize?: string; basis?: string }): Promise<QboReport> {
    const q = new URLSearchParams({ from: params.from, to: params.to, summarize: params.summarize ?? 'month', basis: params.basis ?? 'accrual' });
    const resp = await this.getAgentClient().get<{ success: boolean; data: QboReport; error?: string }>(`/api/qbo/reports/pnl?${q}`);
    if (!resp.data.success) throw new Error(resp.data.error || 'QuickBooks P&L failed');
    return resp.data.data;
  }

  async getQboBalanceSheet(params: { asOf: string; months?: number; basis?: string }): Promise<QboReport> {
    const q = new URLSearchParams({ asOf: params.asOf, months: String(params.months ?? 1), basis: params.basis ?? 'accrual' });
    const resp = await this.getAgentClient().get<{ success: boolean; data: QboReport; error?: string }>(`/api/qbo/reports/balance-sheet?${q}`);
    if (!resp.data.success) throw new Error(resp.data.error || 'QuickBooks balance sheet failed');
    return resp.data.data;
  }

  async getARDrilldown(code: number): Promise<ARDrilldownResponse> {
    return this.agentRecipe<ARDrilldownResponse>('analytics-ar-drilldown', { code: String(code) });
  }

  async getCustomerInvoice(invoice: string): Promise<CustomerInvoiceResponse> {
    return this.agentRecipe<CustomerInvoiceResponse>('customer-invoice', { invoice });
  }

  /** Send a customer-facing AR document email (statement / invoice). Demo tenant only for now. */
  async sendCustomerDoc(payload: {
    to: string[]; cc?: string[]; subject: string; html: string;
    attachments?: { filename: string; contentBase64: string; type?: string }[];
  }): Promise<{ sent: boolean; messageId?: string; recipients: number }> {
    const resp = await this.client.post<{ success: boolean; data: { sent: boolean; messageId?: string; recipients: number }; error?: string }>(
      '/.netlify/functions/send-customer-doc', payload,
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'send-customer-doc failed');
    return resp.data.data;
  }

  // --- Sales by Rep (agent v3.3.2+) ---
  async getSalesReps(): Promise<{ reps: string[] }> {
    return this.agentRecipe<{ reps: string[] }>('sales-reps', {});
  }

  async getSalesByRepKpis(params: {
    rep: string;
    weeks?: number;
    priceItems?: string;  // CSV of item codes
    itemCategory?: string;     // agent 3.22.0+
    itemSubcategory?: string;
  }): Promise<SalesByRepKpisResponse> {
    return this.agentRecipe<SalesByRepKpisResponse>('sales-by-rep-kpis', {
      rep: params.rep,
      weeks: params.weeks != null ? String(params.weeks) : undefined,
      priceItems: params.priceItems,
      itemCategory: params.itemCategory || undefined,
      itemSubcategory: params.itemSubcategory || undefined,
    });
  }

  async getSalesByRepTrend(params: {
    rep: string;
    weeks?: number;
    itemCategory?: string;     // agent 3.22.0+
    itemSubcategory?: string;
  }): Promise<SalesByRepTrendResponse> {
    return this.agentRecipe<SalesByRepTrendResponse>('sales-by-rep-trend', {
      rep: params.rep,
      weeks: params.weeks != null ? String(params.weeks) : undefined,
      itemCategory: params.itemCategory || undefined,
      itemSubcategory: params.itemSubcategory || undefined,
    });
  }

  // --- Inventory replenishment (agent v3.3.15+) ---
  async getInventoryActionQueue(params: {
    buckets?: string;   // CSV: "reorder_now,schedule_now" or "all"
    vendor?: string;
    search?: string;
    limit?: number;
    upcomingSeason?: boolean; // override buckets: SKUs with an active window + YoY>0
    category?: string;        // Invent.Category exact (agent 3.21.0+)
    subCategory?: string;     // Invent.Subcat exact (agent 3.21.0+)
  } = {}): Promise<InventoryActionQueueResponse> {
    return this.agentRecipe<InventoryActionQueueResponse>('inventory-action-queue', {
      buckets: params.buckets,
      vendor:  params.vendor,
      search:  params.search,
      limit:   params.limit != null ? String(params.limit) : undefined,
      upcomingSeason: params.upcomingSeason ? 'true' : undefined,
      category:    params.category || undefined,
      subCategory: params.subCategory || undefined,
    });
  }

  async getInventoryTransitionFeed(params: {
    hours?: number;
    sku?: string;
    vendor?: string;
    bucket?: string;
    trigger?: string;
    limit?: number;
  } = {}): Promise<InventoryTransitionFeedResponse> {
    return this.agentRecipe<InventoryTransitionFeedResponse>('inventory-transition-feed', {
      hours:   params.hours != null ? String(params.hours) : undefined,
      sku:     params.sku,
      vendor:  params.vendor,
      bucket:  params.bucket,
      trigger: params.trigger,
      limit:   params.limit != null ? String(params.limit) : undefined,
    });
  }

  async getInventorySkuDetail(sku: string): Promise<InventorySkuDetailResponse> {
    return this.agentRecipe<InventorySkuDetailResponse>('inventory-sku-detail', { sku });
  }

  /**
   * Last classifier cycle summary. Used to render the "Last refreshed N min ago,
   * <N> SKUs, <N> transitions in cycle" status strip.
   */
  async getInventoryStatus(): Promise<InventoryStatusResponse> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: InventoryStatusResponse; error?: string }>(
      '/api/inventory/status'
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Inventory status failed');
    return resp.data.data;
  }

  /**
   * Manually trigger a classification cycle. v3.6.23: fire-and-forget — the
   * server returns immediately (202) and runs the cycle in the background;
   * poll getInventoryStatus() to watch running -> complete. No-ops server-side
   * if a cycle is already in flight (`alreadyRunning: true`).
   */
  async classifyInventory(): Promise<{ started: boolean; running: boolean; alreadyRunning: boolean }> {
    const resp = await this.getAgentClient().post<{ success: boolean; data: { started: boolean; running: boolean; alreadyRunning: boolean }; error?: string }>(
      '/api/inventory/classify'
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Classification failed');
    return resp.data.data;
  }

  /**
   * Phase A — set or clear the operator override for a SKU's lead time.
   * Pass `null` to clear (revert to Invent.Leadtime). Range 0..365.
   * The engine doesn't run an immediate re-classify; the next 5-minute
   * background cycle picks up the new value. Frontend should optimistically
   * update the cell on success.
   */
  async patchInventorySku(skuId: string, body: { leadTimeDaysManual?: number | null }):
    Promise<{ skuId: string; leadTimeDaysManual: number | null }>
  {
    const resp = await this.getAgentClient().patch<{ success: boolean; data: { skuId: string; leadTimeDaysManual: number | null }; error?: string }>(
      `/api/inventory/sku/${encodeURIComponent(skuId)}`,
      body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Patch failed');
    return resp.data.data;
  }

  /**
   * v3.6.16 — set/clear the manual "In Process" flag for a SKU. Writes to
   * Advantage (InventSub.MfrDateItem) and optimistically flips the admin DB,
   * so the queue reflects it before the next classifier cycle. Frontend should
   * optimistically update + invalidate the queue on success.
   */
  async setInventoryInProcess(skuId: string, inProcess: boolean):
    Promise<{ skuId: string; inProcess: boolean; advantagePersisted: boolean }>
  {
    const resp = await this.getAgentClient().post<{ success: boolean; data: { skuId: string; inProcess: boolean; advantagePersisted: boolean }; error?: string }>(
      `/api/inventory/sku/${encodeURIComponent(skuId)}/in-process`,
      { inProcess }
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'In-process update failed');
    return resp.data.data;
  }

  /**
   * Change history for one SKU — who toggled In Process / set the lead-time
   * override, and when. Lazy-loaded when the History panel on the SKU detail
   * is opened. occurredAt is UTC (no 'Z'); render via fmtLocalTime.
   */
  async getSkuHistory(skuId: string): Promise<AuditHistoryEvent[]> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: AuditHistoryEvent[]; error?: string }>(
      `/api/inventory/sku/${encodeURIComponent(skuId)}/history`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'SKU history failed');
    return resp.data.data;
  }

  /**
   * Daily LLM briefing for the buyer. Returns null when the service is
   * disabled (no API key configured) — frontend should hide the banner in
   * that case rather than render an error. `force=true` bypasses the agent's
   * cache and regenerates immediately (costs a fresh Anthropic call).
   */
  async getInventoryInsight(force = false): Promise<InventoryInsightResponse | null> {
    try {
      const resp = await this.getAgentClient().get<{ success: boolean; data: InventoryInsightResponse; error?: string }>(
        `/api/inventory/insight${force ? '?force=true' : ''}`
      );
      if (!resp.data.success) return null;
      return resp.data.data;
    } catch (err: any) {
      // 404 => insight feature disabled on this tenant; quietly fall through.
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  // --- Container module (agent v3.6.0+) ---
  //
  // CRUD for the Purchasing Pipeline page. All endpoints go direct to agent
  // (writes back to Advantage shpschd inside the same call; the round-trip
  // can take a few seconds for multi-line containers).

  async listContainers(status?: string): Promise<ContainerSummary[]> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const resp = await this.getAgentClient().get<{ success: boolean; data: ContainerSummary[]; error?: string }>(
      `/api/containers${q}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'List containers failed');
    return resp.data.data;
  }

  async getContainer(id: string): Promise<ContainerDetail> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: ContainerDetail; error?: string }>(
      `/api/containers/${encodeURIComponent(id)}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Get container failed');
    return resp.data.data;
  }

  /**
   * Create a container with N lines. Each line back-writes to Advantage
   * shpschd; failures are surfaced per-line on the returned detail (don't
   * roll back the whole container). `shpschdFailures` counts the lines
   * that didn't make it to Advantage.
   */
  async createContainer(body: ContainerCreateRequest):
    Promise<{ detail: ContainerDetail; shpschdFailures: number }>
  {
    const resp = await this.getAgentClient().post<{
      success: boolean; data: ContainerDetail; shpschdFailures: number; error?: string
    }>('/api/containers', body);
    if (!resp.data.success) throw new Error(resp.data.error || 'Create container failed');
    return { detail: resp.data.data, shpschdFailures: resp.data.shpschdFailures ?? 0 };
  }

  async patchContainer(id: string, body: ContainerPatchRequest): Promise<ContainerDetail> {
    const resp = await this.getAgentClient().patch<{ success: boolean; data: ContainerDetail; error?: string }>(
      `/api/containers/${encodeURIComponent(id)}`, body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Patch container failed');
    return resp.data.data;
  }

  async deleteContainer(id: string): Promise<{ shpschdErrors: string[] }> {
    const resp = await this.getAgentClient().delete<{
      success: boolean; data: { id: string; shpschdErrors: string[] }; error?: string
    }>(`/api/containers/${encodeURIComponent(id)}`);
    if (!resp.data.success) throw new Error(resp.data.error || 'Delete container failed');
    return { shpschdErrors: resp.data.data.shpschdErrors };
  }

  async addContainerLine(id: string, line: ContainerCreateLine): Promise<ContainerDetail> {
    const resp = await this.getAgentClient().post<{ success: boolean; data: ContainerDetail; error?: string }>(
      `/api/containers/${encodeURIComponent(id)}/lines`, line
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Add line failed');
    return resp.data.data;
  }

  async deleteContainerLine(id: string, poNumber: string, itemNo: string): Promise<ContainerDetail> {
    const resp = await this.getAgentClient().delete<{ success: boolean; data: ContainerDetail; error?: string }>(
      `/api/containers/${encodeURIComponent(id)}/lines/${encodeURIComponent(poNumber)}/${encodeURIComponent(itemNo)}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Delete line failed');
    return resp.data.data;
  }

  /**
   * Change history for one container — who created / updated it, added or
   * removed lines, and when. Lazy-loaded when the History panel on the
   * container detail is opened. occurredAt is UTC (no 'Z'); render via
   * fmtLocalTime.
   */
  async getContainerHistory(id: string): Promise<AuditHistoryEvent[]> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: AuditHistoryEvent[]; error?: string }>(
      `/api/containers/${encodeURIComponent(id)}/history`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Container history failed');
    return resp.data.data;
  }

  /**
   * #2 Purchasing pipeline builder — for one item, the open vendor POs (open qty
   * + vendor name + ETA) and drop-ship candidates (open customer orders). Used by
   * the Purchasing tab to start a shipment: pick a PO → auto-fill qty + vendor.
   */
  async getPurchasingPipeline(item: string): Promise<PurchasingPipelineResponse> {
    return this.agentRecipe<PurchasingPipelineResponse>('purchasing-pipeline', { item });
  }

  // --- AI Orders review queue ---
  //
  // Orders captured by the AI intake channels (voice/email). All endpoints
  // go direct to agent. Approve/reject return 409 when the order is no
  // longer pending_review (or has no customer assigned) — surface the
  // server's error message inline rather than swallowing it.

  async getAiOrders(status?: string, source?: string): Promise<AiOrderSummary[]> {
    const p = new URLSearchParams();
    if (status) p.append('status', status);
    if (source) p.append('source', source);
    const q = p.toString() ? `?${p}` : '';
    const resp = await this.getAgentClient().get<{ success: boolean; data: AiOrderSummary[]; error?: string }>(
      `/api/ai-orders${q}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'List AI orders failed');
    return resp.data.data;
  }

  async getAiOrder(id: string): Promise<AiOrderDetail> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: AiOrderDetail; error?: string }>(
      `/api/ai-orders/${encodeURIComponent(id)}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Get AI order failed');
    return resp.data.data;
  }

  /**
   * Partial update. `lines`, when present, REPLACES the whole line array;
   * the server recomputes ext/subtotal/tax/total — don't recompute client-side.
   */
  async patchAiOrder(id: string, body: AiOrderPatchRequest): Promise<AiOrderDetail> {
    const resp = await this.getAgentClient().patch<{ success: boolean; data: AiOrderDetail; error?: string }>(
      `/api/ai-orders/${encodeURIComponent(id)}`, body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Update AI order failed');
    return resp.data.data;
  }

  async approveAiOrder(id: string, notes?: string): Promise<AiOrderDetail> {
    const resp = await this.getAgentClient().post<{ success: boolean; data: AiOrderDetail; error?: string }>(
      `/api/ai-orders/${encodeURIComponent(id)}/approve`, notes ? { notes } : {}
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Approve failed');
    return resp.data.data;
  }

  async rejectAiOrder(id: string, reason: string): Promise<AiOrderDetail> {
    const resp = await this.getAgentClient().post<{ success: boolean; data: AiOrderDetail; error?: string }>(
      `/api/ai-orders/${encodeURIComponent(id)}/reject`, { reason }
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Reject failed');
    return resp.data.data;
  }

  // --- Financials (QuickBooks Desktop module) ---
  //
  // P&L / expense / aging data come from agent recipes over the QBD cache
  // tables; status + sync are direct endpoints on the agent's QBD module.
  // Recipe params are strings — numbers are stringified before sending.

  async getQbdPnlSummary(params: { months?: number } = {}): Promise<QbdPnlSummaryResponse> {
    return this.agentRecipe<QbdPnlSummaryResponse>('qbd-pnl-summary', {
      months: params.months != null ? String(params.months) : undefined,
    });
  }

  /** COGS & expense KPIs, same month/YTD across years (recipe: qbd-kpi-panel, agent 3.20.0+). */
  async getQbdKpiPanel(params: { month?: string; period?: string; years?: number } = {}): Promise<QbdKpiPanelResponse> {
    return this.agentRecipe<QbdKpiPanelResponse>('qbd-kpi-panel', {
      month:  params.month,
      period: params.period,
      years:  params.years != null ? String(params.years) : undefined,
    });
  }

  async getQbdPnl(params: { dateFrom?: string; dateTo?: string; version?: string } = {}): Promise<QbdPnlResponse> {
    return this.agentRecipe<QbdPnlResponse>('qbd-pnl', {
      dateFrom: params.dateFrom,
      dateTo:   params.dateTo,
      version:  params.version,
    });
  }

  async getQbdBalanceSheet(): Promise<QbdBalanceSheetResponse> {
    return this.agentRecipe<QbdBalanceSheetResponse>('qbd-balance-sheet', {});
  }

  async getQbdBudgetVsActual(params: { year?: number } = {}): Promise<QbdBudgetVsActualResponse> {
    return this.agentRecipe<QbdBudgetVsActualResponse>('qbd-budget-vs-actual', {
      year: params.year != null ? String(params.year) : undefined,
    });
  }

  async getQbdExpenseBreakdown(params: { dateFrom?: string; dateTo?: string } = {}): Promise<QbdExpenseBreakdownResponse> {
    return this.agentRecipe<QbdExpenseBreakdownResponse>('qbd-expense-breakdown', {
      dateFrom: params.dateFrom,
      dateTo:   params.dateTo,
    });
  }

  async getQbdArAging(): Promise<QbdAgingResponse> {
    return this.agentRecipe<QbdAgingResponse>('qbd-ar-aging', {});
  }

  async getQbdApAging(): Promise<QbdAgingResponse> {
    return this.agentRecipe<QbdAgingResponse>('qbd-ap-aging', {});
  }

  async getQbdPayrollSummary(params: { months?: number } = {}): Promise<QbdPayrollSummaryResponse> {
    return this.agentRecipe<QbdPayrollSummaryResponse>('qbd-payroll-summary', {
      months: params.months != null ? String(params.months) : undefined,
    });
  }

  async getQbdPayrollEmployees(params: { months?: number } = {}): Promise<QbdPayrollEmployeesResponse> {
    return this.agentRecipe<QbdPayrollEmployeesResponse>('qbd-payroll-employees', {
      months: params.months != null ? String(params.months) : undefined,
    });
  }

  async getQbdTimecards(params: { dateFrom?: string; dateTo?: string; dept?: string } = {}): Promise<QbdTimecardsResponse> {
    return this.agentRecipe<QbdTimecardsResponse>('qbd-timecards', {
      dateFrom: params.dateFrom,
      dateTo:   params.dateTo,
      dept:     params.dept,
    });
  }

  /**
   * QBD module status — connection mode, company file, sync interval, and
   * the last sync run. 404s when the module isn't enabled on this tenant's
   * agent; the Connection tab renders a friendly panel in that case.
   */
  async getQbdStatus(): Promise<QbdStatusResponse> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: QbdStatusResponse; error?: string }>(
      '/api/qbd/status'
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'QBD status failed');
    return resp.data.data;
  }

  /** Manually trigger a QBD sync. Returns the run result when it completes. */
  async runQbdSync(): Promise<QbdSyncResponse> {
    const resp = await this.getAgentClient().post<QbdSyncResponse>('/api/qbd/sync');
    if (!resp.data.success) throw new Error(resp.data.result?.error || 'QBD sync failed');
    return resp.data;
  }

  // --- P&L adjustments (agent /api/qbd/adjustments) ---
  // Manual tweaks folded into the "Modified" P&L view; managed from the
  // P&L tab's Adjustments modal.

  async getQbdAdjustments(): Promise<QbdAdjustment[]> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: QbdAdjustment[]; error?: string }>(
      '/api/qbd/adjustments'
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Adjustments load failed');
    return resp.data.data;
  }

  async putQbdAdjustment(body: { id?: string; account: string; acctType: string; ym: string; amount: number; memo: string }): Promise<{ id: string }> {
    const resp = await this.getAgentClient().put<{ success: boolean; data: { id: string }; error?: string }>(
      '/api/qbd/adjustments', body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Adjustment save failed');
    return resp.data.data;
  }

  async deleteQbdAdjustment(id: string): Promise<void> {
    const resp = await this.getAgentClient().delete<{ success: boolean; error?: string }>(
      `/api/qbd/adjustments/${encodeURIComponent(id)}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Adjustment delete failed');
  }

  // --- Budget (agent /api/qbd/budget) ---
  // Raw per-account monthly budget rows + cell edits + year generation;
  // the qbd-budget-vs-actual recipe reads these back for the Budget tab.

  async getQbdBudget(year: number): Promise<QbdBudgetRow[]> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: QbdBudgetRow[]; error?: string }>(
      `/api/qbd/budget?year=${year}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Budget load failed');
    return resp.data.data;
  }

  async putQbdBudgetCell(body: { account: string; acctType: string; ym: string; amount: number }): Promise<void> {
    const resp = await this.getAgentClient().put<{ success: boolean; error?: string }>(
      '/api/qbd/budget', body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Budget cell save failed');
  }

  async generateQbdBudget(body: { year: number; baseYear: number; upliftPct: number }): Promise<void> {
    const resp = await this.getAgentClient().post<{ success: boolean; error?: string }>(
      '/api/qbd/budget/generate', body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Budget generation failed');
  }

  // --- Cache Freshness (agent v3.2.0+) ---
  // Direct to agent: lightweight, never touches Advantage, safe to poll on a
  // global timer. Used by the app-level freshness banner.
  async getCacheFreshness(): Promise<CacheFreshnessResponse> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: CacheFreshnessResponse; error?: string }>(
      '/api/cache/freshness'
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Freshness check failed');
    return resp.data.data;
  }

  // --- Agent Status (admin) ---
  // v3: cache info is embedded in `health.cache`; recipes are baked into the
  // agent exe so there's no separate recipe list/sync.
  async getAgentStatus(): Promise<{ health: Record<string, unknown> }> {
    return (await this.client.get<ApiResponse<{ health: Record<string, unknown> }>>('/.netlify/functions/agent-status')).data.data;
  }

  async triggerCacheSync(): Promise<Record<string, unknown>> {
    return (await this.client.get<ApiResponse<Record<string, unknown>>>('/.netlify/functions/agent-status?action=cache-sync')).data.data;
  }

  async triggerAgentUpdate(apply = false): Promise<Record<string, unknown>> {
    return (await this.client.get<ApiResponse<Record<string, unknown>>>(`/.netlify/functions/agent-status?action=update&apply=${apply}`)).data.data;
  }

  // --- Export ---
  async exportReport(type: string, format: 'excel' | 'pdf', filters?: Filters): Promise<Blob> {
    const p = new URLSearchParams();
    p.append('type', type);
    p.append('format', format);
    if (filters?.salesman) p.append('salesman', filters.salesman);
    if (filters?.category) p.append('category', filters.category);
    const res = await this.client.get(`/.netlify/functions/export?${p}`, { responseType: 'blob' });
    return res.data;
  }

  // --- Route Snapshot (direct to agent — renders can take 30s+) ---
  // Targets agent endpoints introduced in v3.1.0+ (route-snapshot + renderer).

  async runRouteSnapshot(anchorDate?: string): Promise<RouteSnapshotResult> {
    const body = anchorDate ? { anchorDate } : {};
    const resp = await this.getAgentClient().post<{ success: boolean; data: RouteSnapshotResult; error?: string }>(
      '/api/route-snapshot/run', body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Snapshot failed');
    return resp.data.data;
  }

  async getRouteSnapshotStatus(): Promise<RouteSnapshotStatus> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: RouteSnapshotStatus; error?: string }>(
      '/api/route-snapshot/status'
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Status check failed');
    return resp.data.data;
  }

  async listReports(): Promise<string[]> {
    const resp = await this.getAgentClient().get<{ success: boolean; data: { reports: string[] }; error?: string }>(
      '/api/report/list'
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'List reports failed');
    return resp.data.data.reports;
  }

  /** Render a report to PDF. Returns the raw PDF bytes as a Blob. */
  async renderReport(name: string, parameters: Record<string, string> = {}): Promise<Blob> {
    const resp = await this.getAgentClient().post(
      '/api/report/render',
      { name, parameters },
      { responseType: 'blob' }
    );
    return resp.data;
  }

  /**
   * Manual trigger: kicks off the same orchestration the cron scheduler runs.
   * `mode` = 'all' (original: every order) or 'unpicked' (batch pre-pick: the load
   * sheet shows only the outstanding quantities and the orders get marked picked).
   */
  async sendNow(sendNumber: 1 | 2, mode: RouteContentMode = 'all', reqDate?: string): Promise<SendNowResult> {
    const body: Record<string, unknown> = { sendNumber, mode };
    if (reqDate) body.reqDate = reqDate;
    const resp = await this.client.post<{ success: boolean; data: SendNowResult; error?: string }>(
      '/.netlify/functions/send-now', body
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'send-now failed');
    return resp.data.data;
  }

  /** Returns the distinct list of driver codes present in the current snapshot. */
  async listRouteDrivers(): Promise<string[]> {
    const resp = await this.getAgentClient().post<{
      success: boolean;
      data: { rows: Array<{ Driver: string }> };
      error?: string;
    }>('/api/query', { sql: 'SELECT DISTINCT Driver FROM RS_Orders ORDER BY Driver' });
    if (!resp.data.success) throw new Error(resp.data.error || 'Driver list failed');
    return resp.data.data.rows.map((r) => r.Driver).filter(Boolean);
  }
}

// Load-sheet content mode: 'all' = original flow (every order); 'unpicked' = batch
// pre-pick (load sheet shows only outstanding qty; orders get marked picked).
export type RouteContentMode = 'all' | 'unpicked';

// Route snapshot DTOs returned by the agent. Mirror RouteSnapshotResult in
// Agent.NET/.../RouteSnapshotSyncEngine.cs.
export interface RouteSnapshotResult {
  nextDeliveryDate: string;
  closingDatesRowCount: number;
  ordersRowCount: number;
  routeNotesRowCount: number;
  durationMs: number;
  ranAt: string;
}

export interface RouteSnapshotStatus {
  enabled: boolean;
  lastRun: RouteSnapshotResult | null;
}

// Returned by the send-now Netlify function. Mirrors OrchestrateResult in
// netlify/functions/lib/orchestrator.ts.
export interface SendNowResult {
  reqDate: string;
  groupRecipientCount: number;
  driverEmailCount: number;
  emailsSent: number;
  emailsFailed: number;
  errors: string[];
  // v2: true when the agent accepted the batch and is running the locked freeze+pick in
  // its background — the email is sent a bit later via the route-send-ready callback, so
  // emailsSent is 0 here and the UI should show "batch started" rather than a 0-sent state.
  deferred?: boolean;
}

export const apiV2 = new ApiV2Service();

export function downloadFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
